import { ctaClasses } from '@/components/bedrock/cta-classes';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';

import { annualMonthlyUsd, formatUsd, type BillingPeriod } from './billing-period';

import type { PricingTier } from './pricing-tiers';
import type { ReactElement } from 'react';

export interface TierCardProps {
  readonly tier: PricingTier;
  /** Which billing period to price the card under (default monthly). */
  readonly billingPeriod?: BillingPeriod;
}

export function TierCard({ tier, billingPeriod = 'monthly' }: TierCardProps): ReactElement {
  const { t } = useT();
  // Annual view only applies to numeric self-serve tiers; Enterprise (no
  // `monthlyUsd`) always shows its custom price under both periods.
  const monthlyUsd = tier.monthlyUsd;
  const isAnnual = billingPeriod === 'annual';
  const showAnnual = isAnnual && monthlyUsd !== undefined;
  const priceLabel =
    isAnnual && monthlyUsd !== undefined
      ? formatUsd(annualMonthlyUsd(monthlyUsd))
      : tier.priceKey !== undefined
        ? t(tier.priceKey)
        : tier.price;
  const baseClasses =
    'flex flex-col gap-4 rounded-[var(--bd-radius)] border p-6 transition focus-within:ring-2 focus-within:ring-[color:var(--bd-blue)]';
  const highlightedClasses =
    'border-foreground bg-[color:var(--bd-page)] shadow-lg ring-1 ring-foreground';
  const restingClasses = 'border-[color:var(--bd-line)] bg-[color:var(--bd-page)]';
  return (
    <li
      data-tier={tier.slug}
      data-highlighted={tier.highlighted ? 'true' : 'false'}
      className={`${baseClasses} ${tier.highlighted ? highlightedClasses : restingClasses}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        {/* No "Most popular" badge: that is a claim about what customers
            chose, and there are none yet (operator decision, 2026-08-18). The
            card keeps its visual emphasis — recommending a tier is ours to do —
            but says nothing about how many people picked it. */}
        <h3 className="text-lg font-semibold text-[color:var(--bd-ink)]">{tier.name}</h3>
      </div>
      <div className="flex items-baseline gap-1">
        {/* numbered tiers keep the price verbatim (ADR-0035); the annual view
            renders the −10% monthly-equivalent; descriptive prices (Enterprise
            → "Custom") localize via `priceKey` like allowance (I133). */}
        <span className="text-3xl font-semibold tracking-tight text-[color:var(--bd-ink)]">
          {priceLabel}
        </span>
        {/* the period is a unit LABEL — localize it like the allowance label
            (I133). Omitted for custom-quoted tiers (no "Custom/yr"). The annual
            view still meters per month (billed annually), so "/mo" holds. */}
        {tier.periodKey !== undefined || tier.period !== undefined ? (
          <span data-period className="text-sm text-[color:var(--bd-muted)]">
            {tier.periodKey !== undefined ? t(tier.periodKey) : tier.period}
          </span>
        ) : null}
      </div>
      {/* Annual disclosure: only on numeric tiers under the annual period. */}
      {showAnnual ? (
        <p data-annual-note className="-mt-2 text-xs text-[color:var(--bd-muted)]">
          {t('pricing.tier.annualNote')}
        </p>
      ) : null}
      <p className="text-xs uppercase tracking-wide text-[color:var(--bd-muted)] rtl:normal-case rtl:tracking-normal">
        {tier.allowanceKey !== undefined ? t(tier.allowanceKey) : tier.allowance}
      </p>
      <ul className="flex flex-col gap-1.5 text-sm text-[color:var(--bd-muted)]">
        {tier.bulletKeys.map((bulletKey) => (
          <li key={bulletKey} className="flex gap-2">
            <span aria-hidden="true" className="text-[color:var(--bd-faint)]">
              ·
            </span>
            <span>{t(bulletKey)}</span>
          </li>
        ))}
      </ul>
      {/* Locale-aware: the CTA targets an internal route (/contact), so it must
          keep the visitor's locale prefix — a bare <a> dropped a French visitor
          onto the English contact page. */}
      {/* The house CTA, not a hand-rolled one. This button never moved when
          `marketing-ui/Button` was folded into bedrock, so it kept a 6px
          radius against bedrock's 10px, its own padding scale, `opacity-90` as
          its hover where every other CTA shifts FILL, and no press or disabled
          state at all.

          The part that showed was the colour. `bg-primary` is repointed to
          `--bd-blue` for this app (`globals.css`), so it rendered brand BLUE —
          which is not the old ember, but is the thing `Cta.tsx` exists to say
          no to: bedrock's primary is an INK fill precisely because spending the
          brand on every button "flattened it into decoration". The featured
          tier's buy button was the one blue-filled primary left on the site.

          `highlighted` maps onto the variant rather than onto a colour: that
          is exactly the primary/secondary distinction bedrock already encodes,
          so the emphasis survives a palette change instead of restating one. */}
      <LocalizedLink
        to={tier.cta.href}
        className={ctaClasses({
          variant: tier.highlighted ? 'primary' : 'secondary',
          size: 'sm',
          className: 'mt-auto w-full',
        })}
      >
        {t(tier.cta.labelKey)}
      </LocalizedLink>
    </li>
  );
}
